import type { CSSProperties } from 'react';
import type {
  DesignElement,
  TextElement,
  ShapeElement,
  ImageElement,
  LineElement,
  TableElement,
  CellTableElement,
  CellTableColumn,
  VoucherTableElement,
} from '@/types';
import type { VoucherCtx } from '@/shared/utils/dataTokens';
import { MM_TO_PX } from '@/shared/utils/paperSizes';
import {
  resolveText,
  resolveLineItemCell,
  AVAILABLE_TOKENS,
  type TokenContext,
} from '@/shared/utils/dataTokens';
import { borderSidesCss } from '@/shared/utils/border';
import {
  resolveVoucherColumns,
  resolveVoucherMetaFields,
} from '@/shared/utils/elementFactory';
import { amountToWordsOrEmpty } from '@/shared/utils/numberToWords';
import {
  isFormula,
  evaluateCellFormula,
  formatCellResult,
} from '@/shared/utils/cellFormula';

// Resolve a single token key to its designer-mode label: [Company Name], etc.
function designerLabel(key: string): string {
  const tok = AVAILABLE_TOKENS.find((t) => t.key === key.trim());
  return tok ? `[${tok.label}]` : `[${key.trim()}]`;
}

// Replace all {{token.key}} in a string with [Label] for designer preview.
function layoutText(text: string): string {
  return text.replace(/\{\{([^}]+)\}\}/g, (_m, key) => designerLabel(key));
}

// Sample line items shown in the designer preview when no real invoice is bound.
const SAMPLE_ITEMS = [
  {
    id: '1',
    description: 'Sample Product A',
    hsnCode: '8471',
    quantity: 2,
    unit: 'pc',
    rate: 1500,
    discountPercent: 0,
    taxPercent: 13,
    amount: 3390,
  },
  {
    id: '2',
    description: 'Service Charge',
    hsnCode: '9983',
    quantity: 1,
    unit: 'hr',
    rate: 2000,
    discountPercent: 10,
    taxPercent: 13,
    amount: 2034,
  },
  {
    id: '3',
    description: 'Sample Product B',
    hsnCode: '8517',
    quantity: 5,
    unit: 'pc',
    rate: 750,
    discountPercent: 0,
    taxPercent: 13,
    amount: 4237.5,
  },
];

const SAMPLE_VOUCHER: VoucherCtx = {
  voucherType: 'Payment',
  voucherNo: 'PMT-033',
  voucherDate: '2026-07-20',
  narration: 'This is a test narration',
  entries: [
    {
      ledgerName: 'Bank Charges ➤ 6509144.30 Dr',
      debitAmount: '1200.00',
      creditAmount: '0.00',
      remarks: '',
    },
    {
      ledgerName: 'Cash ➤ 6509144.30 Dr',
      debitAmount: '0.00',
      creditAmount: '1000.00',
      remarks: '',
    },
    {
      ledgerName: 'Computers ➤ 100.00 Dr',
      debitAmount: '0.00',
      creditAmount: '200.00',
      remarks: '',
    },
  ],
};

interface ElementRendererProps {
  el: DesignElement;
  ctx: TokenContext;
  selected?: boolean;
  printMode?: boolean;
  itemsPerPage?: number;
  currencyPrefix?: string;
}

export function ElementRenderer({
  el,
  ctx,
  selected: _selected,
  printMode,
  itemsPerPage,
  currencyPrefix,
}: ElementRendererProps) {
  switch (el.type) {
    case 'text':
      return (
        <TextRenderer
          el={el}
          ctx={ctx}
          printMode={printMode}
          currencyPrefix={currencyPrefix}
        />
      );
    case 'shape':
      return <ShapeRenderer el={el} />;
    case 'image':
      return <ImageRenderer el={el} ctx={ctx} printMode={printMode} />;
    case 'line':
      return <LineRenderer el={el} />;
    case 'table':
      return (
        <TableRenderer
          el={el}
          ctx={ctx}
          printMode={printMode}
          itemsPerPage={itemsPerPage}
          currencyPrefix={currencyPrefix}
        />
      );
    case 'cellTable':
      return <CellTableRenderer el={el} ctx={ctx} printMode={printMode} />;
    case 'voucherTable':
      return (
        <VoucherTableRenderer
          el={el}
          ctx={ctx}
          printMode={printMode}
          currencyPrefix={currencyPrefix}
        />
      );
  }
}

// ============================================================
// Text
// ============================================================
function TextRenderer({
  el,
  ctx,
  printMode,
  currencyPrefix,
}: {
  el: TextElement;
  ctx: TokenContext;
  printMode?: boolean;
  currencyPrefix?: string;
}) {
  const resolved = printMode
    ? el.dataToken
      ? resolveText(`{{${el.dataToken}}}`, ctx)
      : resolveText(el.content, ctx)
    : el.dataToken
      ? designerLabel(el.dataToken)
      : layoutText(el.content);

  // "Amount in Words": convert the resolved amount to words at print time only.
  // Non-numeric values become '' (print nothing). In the designer we keep the
  // [Label]/content preview so the bound field stays visible while designing.
  const text =
    el.amountInWords && printMode
      ? amountToWordsOrEmpty(resolved, currencyPrefix)
      : resolved;

  const padPx = (el.padding ?? 0) * MM_TO_PX;
  const borderActive = !!(el.borderEnabled && el.borderWidth && el.borderColor);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: el.fontFamily,
        fontSize: `${el.fontSize}pt`,
        fontWeight: el.fontWeight,
        fontStyle: el.italic ? 'italic' : 'normal',
        textDecoration: el.underline ? 'underline' : 'none',
        color: el.color,
        textAlign: el.align,
        lineHeight: el.lineHeight,
        letterSpacing: `${el.letterSpacing}em`,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflow: 'hidden',
        backgroundColor:
          el.backgroundEnabled && el.background ? el.background : 'transparent',
        ...(borderActive
          ? borderSidesCss(
              el.borderSides,
              el.borderWidth ?? 0,
              el.borderColor ?? '#000',
            )
          : {}),
        borderRadius: el.borderRadius ? `${el.borderRadius}px` : undefined,
        padding: padPx ? `${padPx}px` : undefined,
        boxSizing: 'border-box',
      }}
    >
      {text}
    </div>
  );
}

// ============================================================
// Shape
// ============================================================
function ShapeRenderer({ el }: { el: ShapeElement }) {
  const common: React.CSSProperties = {
    width: '100%',
    height: '100%',
    backgroundColor: el.fillEnabled ? el.fill : 'transparent',
    boxSizing: 'border-box',
  };
  if (el.strokeEnabled) {
    if (el.shape === 'ellipse') {
      // Per-side borders don't make sense on a curved edge — always full.
      common.border = `${el.strokeWidth}px solid ${el.stroke}`;
    } else {
      Object.assign(
        common,
        borderSidesCss(el.borderSides, el.strokeWidth, el.stroke),
      );
    }
  }
  if (el.shape === 'ellipse') {
    common.borderRadius = '50%';
  } else {
    common.borderRadius = `${el.borderRadius}px`;
  }
  return <div style={common} />;
}

// ============================================================
// Image
// ============================================================
function ImageRenderer({
  el,
  ctx,
  printMode,
}: {
  el: ImageElement;
  ctx: TokenContext;
  printMode?: boolean;
}) {
  const borderActive = !!(el.borderEnabled && el.borderWidth && el.borderColor);
  const wrapperStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    borderRadius: el.borderRadius ? `${el.borderRadius}px` : undefined,
    ...(borderActive
      ? borderSidesCss(
          el.borderSides,
          el.borderWidth ?? 0,
          el.borderColor ?? '#000',
        )
      : {}),
  };

  // ── All item images — one box per image, code below each, gap between boxes ──
  if (el.dataToken === 'items.images') {
    if (!printMode) {
      return (
        <div
          className="w-full h-full bg-paper-200 border border-dashed border-ink-300 flex items-center justify-center text-[10px] text-ink-400"
          style={borderActive ? wrapperStyle : undefined}
        >
          {`Item Images (${el.imagesPerRow ?? 3} per row)`}
        </div>
      );
    }

    const items: any[] = ctx.details ?? [];

    const codeFontSize = el.itemCodeFontSize ?? 8;
    const gap = el.imageGap ?? 4;
    const imagesPerRow = el.imagesPerRow ?? 3;

    // Flatten: one entry per individual image — skip items/images with no valid URL
    const entries: { url: string; code: string }[] = [];
    items.forEach((item) => {
      const rawImages: any[] = (item.images as any) ?? [];
      const code: string = item.itemCode ?? item.code ?? '';
      rawImages.forEach((img: any) => {
        const url = typeof img === 'string' ? img : img?.image;
        if (url) entries.push({ url, code });
      });
    });

    // No images from backend — render nothing, not even an empty box
    if (entries.length === 0) return null;

    const numRows = Math.ceil(entries.length / imagesPerRow);

    return (
      <div
        style={{
          ...wrapperStyle,
          display: 'grid',
          gridTemplateColumns: `repeat(${imagesPerRow}, 1fr)`,
          gridTemplateRows: `repeat(${numRows}, 1fr)`,
          gap: `${gap}px`,
        }}
      >
        {entries.map(({ url, code }, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <img
              src={url}
              alt=""
              style={{
                width: '100%',
                flex: 1,
                minHeight: 0,
                objectFit: el.objectFit,
                display: 'block',
              }}
              draggable={false}
            />
            {code && (
              <div
                style={{
                  fontSize: `${codeFontSize}pt`,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  lineHeight: 1.4,
                  flexShrink: 0,
                  paddingTop: '1px',
                }}
              >
                {code}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (el.dataToken === 'documentImage') {
    if (!printMode) {
      return (
        <div
          className="w-full h-full bg-paper-200 border border-dashed border-ink-300 flex items-center justify-center text-[10px] text-ink-400"
          style={borderActive ? wrapperStyle : undefined}
        >
          {`KYC Document Images (${el.imagesPerRow ?? 3} per row)`}
        </div>
      );
    }

    const documents: any[] = (ctx.summary as any)?.kyc?.documents ?? [];
    const urls = documents
      .map((doc) => (typeof doc === 'string' ? doc : doc?.image))
      .filter(Boolean);

    if (urls.length === 0) return null;

    const imagesPerRow = el.imagesPerRow ?? 3;
    const numRows = Math.ceil(urls.length / imagesPerRow);

    return (
      <div
        style={{
          ...wrapperStyle,
          display: 'grid',
          gridTemplateColumns: `repeat(${imagesPerRow}, 1fr)`,
          gridTemplateRows: `repeat(${numRows}, 1fr)`,
          gap: `${el.imageGap ?? 4}px`,
        }}
      >
        {urls.map((url, i) => (
          <img
            key={i}
            src={url}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              minWidth: 0,
              minHeight: 0,
              objectFit: el.objectFit,
              display: 'block',
            }}
            draggable={false}
          />
        ))}
      </div>
    );
  }

  // ── Single image (uploaded or single-index token) ──────────────────────────
  let src = el.src;
  if (el.dataToken && printMode) {
    src = resolveText(`{{${el.dataToken}}}`, ctx);
  }

  if (!src || src.startsWith('{{')) {
    return (
      <div
        className="w-full h-full bg-paper-200 border border-dashed border-ink-300 flex items-center justify-center text-[10px] text-ink-400"
        style={borderActive ? wrapperStyle : undefined}
      >
        {el.dataToken ? `{{${el.dataToken}}}` : 'No image'}
      </div>
    );
  }
  return (
    <div style={wrapperStyle}>
      <img
        src={src}
        alt=""
        style={{
          width: '100%',
          height: '100%',
          objectFit: el.objectFit,
          display: 'block',
        }}
        draggable={false}
      />
    </div>
  );
}

// ============================================================
// Line
// ============================================================
function LineRenderer({ el }: { el: LineElement }) {
  return (
    <div
      style={{
        width: '100%',
        height: `${el.thickness * MM_TO_PX}px`,
        backgroundColor: el.style === 'solid' ? el.color : 'transparent',
        borderTop:
          el.style !== 'solid'
            ? `${el.thickness * MM_TO_PX}px ${el.style} ${el.color}`
            : 'none',
        marginTop: 'auto',
        marginBottom: 'auto',
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
      }}
    />
  );
}

// ============================================================
// Table — renders the line items grid
// ============================================================
function TableRenderer({
  el,
  ctx,
  printMode,
  itemsPerPage,
  currencyPrefix,
}: {
  el: TableElement;
  ctx: TokenContext;
  printMode?: boolean;
  itemsPerPage?: number;
  currencyPrefix?: string;
}) {
  // The fixed number of rows the table should always occupy (the
  // "items per page" the user configured). When the real data has fewer
  // items than this, the remainder is filled with blank rows so the table
  // keeps its size and layout regardless of how many items were sold.
  const targetRows =
    typeof itemsPerPage === 'number' && itemsPerPage > 0
      ? itemsPerPage
      : undefined;

  // Determine which rows to render.
  // In print mode: real invoice/details data, padded with blank rows up to targetRows.
  // In designer mode: placeholder rows (no actual sample values — cells show [key]).
  type Row =
    | (typeof SAMPLE_ITEMS)[number]
    | import('@/types').LineItem
    | import('@/types').JewelryDetailItem;
  /** Build `count - dataLen` blank filler rows starting at index `dataLen`. */
  const fillerRows = (dataLen: number, count: number): Row[] =>
    Array.from(
      { length: Math.max(0, count - dataLen) },
      (_, i) =>
        ({ id: `empty-${dataLen + i}`, __empty: true }) as unknown as Row,
    );

  let dataRows: Row[] = [];
  if (ctx.details && ctx.details.length > 0) {
    dataRows = ctx.details;
  } else if (ctx.invoice && ctx.invoice.items.length > 0) {
    dataRows = ctx.invoice.items;
  }

  let items: Row[];
  if (dataRows.length > 0) {
    // Real data — pad up to targetRows with blank rows when there are fewer items.
    items = targetRows
      ? [...dataRows, ...fillerRows(dataRows.length, targetRows)]
      : dataRows;
  } else if (printMode) {
    // No data on the real bill — still reserve the configured number of blank rows.
    items = targetRows ? fillerRows(0, targetRows) : [];
  } else {
    // Designer preview — create the right number of placeholder rows (cells show [key]).
    const count = targetRows ?? 3;
    items = Array.from(
      { length: count },
      (_, i) => ({ id: `preview-${i}` }) as unknown as Row,
    );
  }

  const totalWidth = el.columns.reduce((s, c) => s + c.widthPercent, 0) || 100;

  // When "items per page" is configured, every row keeps a fixed height. Rows
  // that don't fit the table box are clipped (overflow hidden below), so extra
  // rows stay hidden until the user manually grows the table height.
  const fixedRowHeightMm = el.rowMinHeight > 0 ? el.rowMinHeight : 7;
  const useFixedRows = targetRows !== undefined && items.length > 0;
  // A headerless legacy table still divides its box evenly across its rows.
  const divideEvenly =
    !useFixedRows && el.showHeader === false && items.length > 0;
  const rowHeight = useFixedRows
    ? `${fixedRowHeightMm * MM_TO_PX}px`
    : divideEvenly
      ? `${100 / items.length}%`
      : undefined;

  return (
    <div
      style={{
        padding: 0,
        margin: 0,
        width: '100%',
        // Shrink-wrap the bordered box to its rows so the border ends right
        // after the last (summary) row — no empty bordered region below it.
        // Only the legacy "divide evenly" mode fills the full element height.
        height: divideEvenly ? '100%' : 'fit-content',
        overflow: 'hidden',
        boxSizing: 'border-box',
        ...borderSidesCss(el.borderSides, el.borderWidth, el.borderColor),
        fontFamily: el.bodyFontFamily,
      }}
    >
      <table
        style={{
          width: '100%',
          height: divideEvenly ? '100%' : undefined,
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        {/* Column widths live here so they apply whether or not the header
            row is rendered. With tableLayout:'fixed' the body cells otherwise
            have no width source when showHeader is false. */}
        <colgroup>
          {el.columns.map((col) => (
            <col
              key={col.id}
              style={{ width: `${(col.widthPercent / totalWidth) * 100}%` }}
            />
          ))}
        </colgroup>
        {el.showHeader !== false && (
          <thead>
            <tr
              style={{
                // With fixed rows the header occupies exactly one row-height so
                // the body still divides evenly into `itemsPerPage` rows.
                height: useFixedRows ? rowHeight : undefined,
                backgroundColor: el.headerBackground,
                fontFamily: el.headerFontFamily,
                fontSize: `${el.headerFontSize}pt`,
                fontWeight: el.headerFontWeight,
                color: el.headerColor,
              }}
            >
              {el.columns.map((col) => (
                <th
                  key={col.id}
                  style={{
                    width: `${(col.widthPercent / totalWidth) * 100}%`,
                    textAlign: col.headerAlign,
                    padding: '4px 6px',
                    borderBottom: `${el.borderWidth}px solid ${el.borderColor}`,
                    borderRight: el.columnDivider
                      ? `${el.borderWidth}px solid ${el.borderColor}`
                      : 'none',
                    fontWeight: el.headerFontWeight,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    verticalAlign: 'bottom',
                  }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody
          style={{
            fontSize: `${el.bodyFontSize}pt`,
            color: el.bodyColor,
          }}
        >
          {items.map((item, idx) => {
            const isEmpty = (item as { __empty?: boolean }).__empty === true;
            return (
              <tr
                key={'id' in item ? item.id : idx}
                style={{
                  height: rowHeight,
                  // Only stripe rows that hold a real item — banding the blank
                  // filler rows leaves ugly empty grey blocks on a short bill.
                  backgroundColor:
                    el.zebra && !isEmpty && idx % 2 === 1
                      ? el.zebraColor
                      : 'transparent',
                }}
              >
                {el.columns.map((col) => (
                  <td
                    key={col.id}
                    style={{
                      textAlign: col.bodyAlign,
                      padding: '4px 6px',
                      borderBottom: el.rowDivider
                        ? `${el.borderWidth}px solid ${el.borderColor}`
                        : 'none',
                      borderRight: el.columnDivider
                        ? `${el.borderWidth}px solid ${el.borderColor}`
                        : 'none',
                      verticalAlign: 'top',
                    }}
                  >
                    <div
                      style={{
                        // With fixed rows the row's own height governs; forcing a
                        // per-cell minHeight here would stack on top of the cell
                        // padding and push rows past the exact box height.
                        minHeight:
                          el.rowMinHeight && !useFixedRows
                            ? `${el.rowMinHeight * MM_TO_PX}px`
                            : undefined,
                      }}
                    >
                      {(item as { __empty?: boolean }).__empty
                        ? ' ' /* blank filler row — keep the cell height */
                        : printMode
                          ? resolveLineItemCell(
                              col.key,
                              item,
                              col.format,
                              idx,
                              currencyPrefix,
                            )
                          : col.key === 'sn' || col.key === '#'
                            ? String(idx + 1)
                            : `[${col.key}]`}
                    </div>
                  </td>
                ))}
              </tr>
            );
          })}
          {el.showSummary && el.columns.length > 0 && (
            <tr style={{ height: rowHeight }}>
              {el.columns.map((col, ci) => {
                const cell = el.summaryCells?.[ci] ?? '';
                const content = cell
                  ? printMode
                    ? resolveText(cell, ctx)
                    : layoutText(cell)
                  : ' ';
                return (
                  <td
                    key={col.id}
                    style={{
                      textAlign: col.bodyAlign,
                      padding: '4px 6px',
                      borderTop: `${el.borderWidth}px solid ${el.borderColor}`,
                      borderRight: el.columnDivider
                        ? `${el.borderWidth}px solid ${el.borderColor}`
                        : 'none',
                      verticalAlign: 'top',
                      fontWeight: el.headerFontWeight,
                    }}
                  >
                    <div
                      style={{
                        minHeight: el.rowMinHeight
                          ? `${el.rowMinHeight * MM_TO_PX}px`
                          : undefined,
                      }}
                    >
                      {content}
                    </div>
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// CellTable — generic table with free-form text cells. No header concept;
// each column independently controls alignment, weight, color, and an
// optional column-wide tint via its CellTableColumn config.
// ============================================================
// Shared per-cell styling — alignment / weight / color / column tint, with the
// column override taking precedence over the table default.
function cellContentStyle(
  el: CellTableElement,
  col: CellTableColumn | undefined,
): CSSProperties {
  return {
    padding: `${el.cellPadding}px`,
    borderBottom: el.showRowBorders
      ? `${el.borderWidth}px solid ${el.borderColor}`
      : 'none',
    borderRight: el.showColumnBorders
      ? `${el.borderWidth}px solid ${el.borderColor}`
      : 'none',
    verticalAlign: 'top',
    textAlign: col?.textAlign ?? el.textAlign,
    color: col?.color ?? el.color,
    fontWeight: col?.fontWeight ?? 400,
    backgroundColor: col?.background ?? undefined,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };
}

function CellTableRenderer({
  el,
  ctx,
  printMode,
}: {
  el: CellTableElement;
  ctx: TokenContext;
  printMode?: boolean;
}) {
  const outerStyle: CSSProperties = {
    width: '100%',
    height: 'fit-content',
    overflow: 'hidden',
    boxSizing: 'border-box',
    ...borderSidesCss(el.borderSides, el.borderWidth, el.borderColor),
    fontFamily: el.fontFamily,
    fontSize: `${el.fontSize}pt`,
    color: el.color,
    margin: 0,
    padding: 0,
  };

  const renderCell = (content: string | undefined, r: number, c: number) => {
    const text = content ?? '';
    if (isFormula(text)) {
      if (!printMode) return text;
      const result = evaluateCellFormula(text, el.cells, ctx);
      return formatCellResult(
        result,
        el.cellFormats?.[`${r}:${c}`],
        el.cellDecimals?.[`${r}:${c}`] ?? 3,
      );
    }
    return printMode ? resolveText(text, ctx) : layoutText(text);
  };

  if (el.perRowColumns && el.rowColumns) {
    return (
      <div style={{ ...outerStyle, display: 'flex', flexDirection: 'column' }}>
        {el.cells.map((row, r) => {
          const cfg = el.rowColumns![r] ?? el.columns;
          return (
            <div
              key={r}
              style={{
                display: 'flex',
                // Each row is content-height so the table grows/shrinks with the
                // number of rows instead of stretching to fill a fixed box.
                flex: '0 0 auto',
                backgroundColor:
                  el.zebra && r % 2 === 1 ? el.zebraColor : 'transparent',
              }}
            >
              {row.map((cellContent, c) => {
                const col = cfg[c];
                return (
                  <div
                    key={c}
                    style={{
                      flex: `0 0 ${col?.widthPercent ?? 100 / row.length}%`,
                      boxSizing: 'border-box',
                      overflow: 'hidden',
                      ...cellContentStyle(el, col),
                    }}
                  >
                    {renderCell(cellContent, r, c)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={outerStyle}>
      <table
        style={{
          width: '100%',
          // Let rows take their content height; the box shrink-wraps to them.
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <colgroup>
          {el.columns.map((col, i) => (
            <col key={i} style={{ width: `${col.widthPercent}%` }} />
          ))}
        </colgroup>
        <tbody>
          {el.cells.map((row, r) => (
            <tr
              key={r}
              style={{
                backgroundColor:
                  el.zebra && r % 2 === 1 ? el.zebraColor : 'transparent',
              }}
            >
              {row.map((cellContent, c) => {
                const col = el.columns[c];
                return (
                  <td key={c} style={cellContentStyle(el, col)}>
                    {renderCell(cellContent, r, c)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function cleanLedgerName(name: string | undefined, strip: boolean): string {
  const n = name ?? '';
  return strip ? n.split('➤')[0].trim() : n;
}

function formatVoucherAmount(value: number): string {
  return Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function VoucherTableRenderer({
  el,
  ctx,
  printMode,
  currencyPrefix,
}: {
  el: VoucherTableElement;
  ctx: TokenContext;
  printMode?: boolean;
  currencyPrefix?: string;
}) {
  const voucher = ctx.voucher ?? (printMode ? null : SAMPLE_VOUCHER);
  const entries = voucher?.entries ?? [];

  const border = `${el.borderWidth}px solid ${el.borderColor}`;

  const rows = entries
    .map((entry) => {
      const debit = Number(entry.debitAmount ?? 0) || 0;
      const credit = Number(entry.creditAmount ?? 0) || 0;
      const isDebit = debit > 0 || (debit === 0 && credit === 0);
      return {
        particular: `${isDebit ? el.drPrefix : el.crPrefix} ${cleanLedgerName(
          entry.ledgerName,
          el.stripLedgerBalance,
        )}`,
        debit,
        credit,
        narration: entry.remarks ?? '',
        isDebit,
        source: {
          ...(entry as unknown as Record<string, unknown>),
          ...(entry.raw ?? {}),
        } as Record<string, unknown>,
      };
    })
    .sort((a, b) => Number(b.isDebit) - Number(a.isDebit));

  const totalDebit = rows.reduce((acc, r) => acc + r.debit, 0);
  const totalCredit = rows.reduce((acc, r) => acc + r.credit, 0);

  const title =
    (el.title && el.title.trim()) ||
    (voucher?.voucherType
      ? `${voucher.voucherType} Voucher`
      : printMode
        ? ''
        : 'Voucher');

  const summaryRecord = (ctx.summary ?? {}) as unknown as Record<
    string,
    unknown
  >;
  const voucherFallback: Record<string, unknown> = {
    voucher_number: voucher?.voucherNo,
    voucher_date: voucher?.voucherDate,
    voucher_type: voucher?.voucherType,
    narration: voucher?.narration,
  };
  const summaryValue = (key: string): string => {
    const v = summaryRecord[key] ?? voucherFallback[key];
    if (v !== undefined && v !== null && v !== '') return String(v);
    return printMode || !key ? '' : `[${key}]`;
  };

  const amountInWords = el.showAmountInWords
    ? amountToWordsOrEmpty(totalDebit, currencyPrefix)
    : '';

  const columns = resolveVoucherColumns(el).map((c) => ({
    ...c,
    width: c.widthPercent,
    align: c.align,
  }));

  const formatCustom = (raw: unknown, format: string | undefined): string => {
    if (raw === undefined || raw === null || raw === '') return '';
    if (format === 'currency' || format === 'number') {
      const n = Number(raw);
      if (isNaN(n)) return String(raw);
      return format === 'currency' ? formatVoucherAmount(n) : String(n);
    }
    return String(raw);
  };

  const cellValue = (
    row: (typeof rows)[number],
    rowIdx: number,
    key: string,
    format: string | undefined,
  ): string => {
    if (key === 'sn') return String(rowIdx + 1);
    if (key === 'particular') return row.particular;
    if (key === 'ledgerName')
      return cleanLedgerName(
        String(row.source.ledgerName ?? ''),
        el.stripLedgerBalance,
      );
    if (key === 'debit')
      return row.debit > 0 ? formatVoucherAmount(row.debit) : '';
    if (key === 'credit')
      return row.credit > 0 ? formatVoucherAmount(row.credit) : '';
    if (key === 'narration') return row.narration;
    if (key === 'drCr') return row.isDebit ? el.drPrefix : el.crPrefix;
    return formatCustom(row.source[key], format);
  };

  const labelKey = columns.some((c) => c.key === 'particular')
    ? 'particular'
    : columns[0]?.key;

  const totalValue = (key: string, format: string | undefined): string => {
    if (key === 'debit') return formatVoucherAmount(totalDebit);
    if (key === 'credit') return formatVoucherAmount(totalCredit);
    if (key === labelKey) return el.totalsLabel;
    if (
      (format === 'currency' || format === 'number') &&
      !['sn', 'particular', 'ledgerName', 'narration', 'drCr'].includes(key)
    ) {
      const sum = rows.reduce(
        (acc, r) => acc + (Number(r.source[key] ?? 0) || 0),
        0,
      );
      return format === 'currency' ? formatVoucherAmount(sum) : String(sum);
    }
    return '';
  };
  const colCount = columns.length;
  const padStr = `${el.cellPadding}px`;

  const bodyCell = (align: CSSProperties['textAlign']): CSSProperties => ({
    padding: padStr,
    borderRight: border,
    borderBottom: border,
    textAlign: align,
    verticalAlign: 'top',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  });

  return (
    <div
      style={{
        width: '100%',
        height: 'fit-content',
        boxSizing: 'border-box',
        borderTop: border,
        borderLeft: border,
        fontFamily: el.fontFamily,
        fontSize: `${el.fontSize}pt`,
        color: el.color,
        margin: 0,
        padding: 0,
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <colgroup>
          {columns.map((c) => (
            <col key={c.id} style={{ width: `${c.width}%` }} />
          ))}
        </colgroup>
        <tbody>
          {el.showTitle && (
            <tr>
              <td
                colSpan={colCount}
                style={{
                  padding: padStr,
                  borderRight: border,
                  borderBottom: border,
                  textAlign: 'center',
                  fontWeight: el.headerFontWeight,
                  fontSize: `${el.titleFontSize}pt`,
                  textTransform: 'uppercase',
                }}
              >
                {title}
              </td>
            </tr>
          )}

          {el.showMeta && (
            <tr>
              <td
                colSpan={colCount}
                style={{
                  padding: padStr,
                  borderRight: border,
                  borderBottom: border,
                  fontWeight: 600,
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  {resolveVoucherMetaFields(el).map((f) => (
                    <span key={f.id}>
                      {f.label ? `${f.label} : ` : ''}
                      {summaryValue(f.key)}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          )}

          <tr
            style={{
              backgroundColor: el.headerBackground,
              fontWeight: el.headerFontWeight,
            }}
          >
            {columns.map((c) => (
              <th
                key={c.id}
                style={{
                  padding: padStr,
                  borderRight: border,
                  borderBottom: border,
                  textAlign: c.align,
                  fontWeight: el.headerFontWeight,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {c.header}
              </th>
            ))}
          </tr>

          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.id} style={bodyCell(c.align)}>
                  {cellValue(row, i, c.key, c.format)}
                </td>
              ))}
            </tr>
          ))}

          {el.showTotals && (
            <tr style={{ fontWeight: el.headerFontWeight }}>
              {columns.map((c) => (
                <td
                  key={c.id}
                  style={{ ...bodyCell(c.align), borderTop: border }}
                >
                  {totalValue(c.key, c.format)}
                </td>
              ))}
            </tr>
          )}

          {el.showLongNarration && (
            <tr>
              <td
                colSpan={colCount}
                style={{
                  padding: padStr,
                  borderRight: border,
                  borderBottom: border,
                  textAlign: 'left',
                }}
              >
                {summaryValue(el.narrationKey ?? 'narration')}
              </td>
            </tr>
          )}

          {el.showAmountInWords && (
            <tr>
              <td
                colSpan={colCount}
                style={{
                  padding: padStr,
                  borderRight: border,
                  borderBottom: border,
                  fontWeight: el.headerFontWeight,
                }}
              >
                {amountInWords}
              </td>
            </tr>
          )}

          {el.showSignatures && el.signatures.length > 0 && (
            <tr>
              <td
                colSpan={colCount}
                style={{
                  padding: padStr,
                  borderRight: border,
                  borderBottom: border,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingTop: 24,
                  }}
                >
                  {el.signatures.map((label, i) => (
                    <span
                      key={i}
                      style={{ flex: 1, textAlign: 'center', fontWeight: 600 }}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
